import '../../../shared/webcomponents/register'

import * as smoothscroll from 'smoothscroll-polyfill'
import * as ReactDOM from 'react-dom'
import * as React from 'react'
import { Provider, type Store } from 'react-redux'
import getMuiTheme from 'material-ui/styles/getMuiTheme'
import MuiThemeProvider from 'material-ui/styles/MuiThemeProvider'
import * as audio from '../../src/utils/audio'
import * as sentry from '../../src/utils/sentry'
import { Pulse } from '../../../app-core/lib/ui/Pulse'
import { initialise as hiveInitialise, globalHive } from '../../src/modules/hive'
import { initStore } from '../../src/modules/initStore'
import { syncAll } from '../../src/redux/actions/entities'
import { selectorsInitialize } from '../../src/redux/selectors'
import { createCoordinatesWatch } from '../../src/redux/services/coordinates'
import { applyTheme } from '../../src/utils/theme'
import { Acti_initialise } from '../Acti/Acti.initialise'
import { Devi_inactivityCheck } from '../Devi/Devi.inactivity'
import { Devi_initialise } from '../Devi/Devi.initialise'
import { Auth_initialise } from '../Auth/Auth.initialise'
import { Auth_loginSync } from '../Auth/Auth.loginSync'
import { Auth_navigateLogin } from '../Auth/Auth.navigate'
import { Auth_getStatus } from '../Auth/Auth.State'
import { Auth_AuthComponent } from '../Auth/Auth.ui.AuthComponent'
import { Auth_safeLogout } from '../Auth/Auth.logout'
import type { Base_Bus } from '../Base/Base.Bus'
import { Base_defaultOperations } from '../Base/Base.IContext'
import { Base_Inject } from '../Base/Base.ui.Inject'
import { keys } from '../Config'
import { Cust_initialise } from '../Cust/Cust.initialise'
import { DS_getData, DS_setData, DS_subscribe, DS_unsubscribe } from '../DS/DS.Data'
import { DS_Listen } from '../DS/DS.ui.Listen'
import { Erro_injectRenderCatchIntoBaseComponent } from '../Erro/Erro.renderCatch'
import { E2E_initialize } from '../E2E/E2E.initialize'
import { Evnt_addHiveToWindow, Evnt_addModulesToWindow } from './Evnt.onLoad.consoleHelpers'
import { Flow_initialise } from '../Flow/Flow.initialise'
import { I18N_initialise } from '../I18N/I18N.initialise'
import { IDB_deviceDBInitialise } from '../IDB/IDB.deviceDB'
import { IDB_dexieInitialise } from '../IDB/IDB.dexie'
import { IDB_initialise } from '../IDB/IDB.initialise'
import { Imag_initialise } from '../Imag/Imag.initialise'
import { Logg_event } from '../Logg/Logg.event'
import { Logg_timer } from '../Logg/Logg.timer'
import { LS_envGet } from '../LS/LS.env'
import { LS_migrate } from '../LS/LS.migrate'
import { LS_serverPrefsGet } from '../LS/LS.serverPrefs'
import { Noti_initialise } from '../Noti/Noti.initialise'
import { Noti_registerMessageListeners } from '../Noti/Noti.registerMessageListeners'
import { Plat_isCordova } from '../Plat/Plat.device'
import { Rpts_initialise } from '../Rpts/Rpts.initialise'
import { Rout_toHome } from '../Rout/Rout.home'
import { Sync_initialise } from '../Sync/Sync.initialise'
import { Todo_initialise } from '../Todo/Todo.initialise'
import { UI_Dialog } from '../UI/UI.Dialog'
import { UI_Snackbar } from '../UI/UI.Snackbar'
import { User_get, User_isLoggedIn } from '../User/User.get'
import * as UserProfileImageCache from '../User/UserProfileImageCache'
import { Unit_initialise } from '../Unit/Unit.initialise'
import { Uplo_initialise } from '../Uplo/initialise'
import { assumeExhaustion } from '../../../shared/lib/typeHelpers.no-deps'
import { Rout_to } from '../Rout/Rout'
import { Plat_recoverPath } from '../Plat/Plat.androidRecovery'
import type { IState } from '../../src/redux/reducers'
import { Translations } from '../../../shared/i18n/Translations'
import { pick } from 'lodash'
import { defaultLanguage } from '../../../shared/i18n/i18n.no-deps'
import { ESystem } from '../../../shared/i18n/i18next-source-files'
import { shouldUseSentry } from '../../../shared/lib/sentry-helpers'
import { Glbl_ZapBot } from '../Glbl/Glbl.ui.ZapBot'
import * as ZendeskWebChat from '../../../app-core/lib/util/zendeskWebChat'
import * as LaunchDarkly from '../Util/Util.launchdarkly'

//
// This is triggered when the DOM is ready and initializing code can be started
//
export async function Evnt_onLoad(createRouter: () => React.ReactElement<unknown>, bus: Base_Bus) {
    // Hide zendesk chat by default
    ZendeskWebChat.hideChat()

    Evnt_addModulesToWindow()
    if (location.search === '?skipOnLoad=true') return

    Logg_event('Start', { label: 'OnLoad' })

    if (!Plat_isCordova() && navigator.serviceWorker) {
        // register service-worker
        navigator.serviceWorker.register('sw.js')
    }

    const reduxStore = initStore()
    const router = createRouter()

    // Start rendering straight away to give user feedback
    renderReactApp(bus, reduxStore, router)

    initApp(bus, reduxStore)
}

const muiTheme = () =>
    getMuiTheme({
        fontFamily: 'var(--base-font)',
        palette: {
            primary1Color: 'var(--primary-color)',
            accent1Color: 'var(--primary-color)',
        },
        tabs: {
            backgroundColor: 'var(--lightGrey)',
            textColor: 'var(--grey6)',
            selectedTextColor: 'var(--darkGrey)',
        },
        checkbox: {
            boxColor: 'var(--grey1)', // these are normally inherited from the base text colour
            labelColor: 'var(--grey1)', // so if we decide to set it to grey1 these can be removed
            checkedColor: 'var(--green)',
        },
        radioButton: {
            borderColor: 'var(--grey1)', // these are normally inherited from the base text colour
            labelColor: 'var(--grey1)', // so if we decide to set it to grey1 these can be removed
            checkedColor: 'var(--green)',
        },
        raisedButton: {
            textColor: 'var(--primary-color)',
            secondaryColor: 'var(--blue)',
        },
        chip: {
            backgroundColor: 'var(--grey6)',
        },
    })

const renderReactApp = (bus: Base_Bus, reduxStore: Store<IState>, router: React.ReactElement<unknown>) => {
    ReactDOM.render(
        <Provider store={reduxStore}>
            <MuiThemeProvider muiTheme={muiTheme()}>
                <Base_Inject
                    data={DS_getData()}
                    dataSubscribe={DS_subscribe}
                    dataUnsubscribe={DS_unsubscribe}
                    operations={Base_defaultOperations(bus)}
                >
                    <div style={{ height: '100%' }}>
                        {DS_Listen(
                            (d) => ({
                                onLoadState: d.onLoad,
                                authState: d.auth,
                                cleanseState: d.cleanse,
                                safeLogout: d.safeLogout,
                            }),
                            ({ onLoadState, authState, cleanseState, safeLogout }) => {
                                // onLoadState
                                if (onLoadState.status === 'loading') return <Pulse />
                                if (onLoadState.status === 'failure') throw onLoadState.error

                                // safeLogout
                                if (safeLogout.status === 'in-progress') return <Pulse />
                                if (safeLogout.status === 'failure') throw safeLogout.error

                                // cleanseState
                                if (cleanseState.status === 'in-progress') return <Pulse />
                                if (cleanseState.status === 'failure') throw cleanseState.error

                                // Auth
                                if (authState.status !== 'complete') return <Auth_AuthComponent />

                                return (
                                    <>
                                        {router}
                                        <Glbl_ZapBot />
                                    </>
                                )
                            }
                        )}
                        <UI_Snackbar />
                        <UI_Dialog />
                    </div>
                </Base_Inject>
            </MuiThemeProvider>
        </Provider>,
        document.getElementById('react-router')
    )
}

const initApp = async (bus: Base_Bus, reduxStore: Store<IState>) => {
    try {
        // Polyfill for smooth scroll behaviour
        smoothscroll.polyfill()

        // We have to wait for the deviceready event before doing things with cordova plugins
        // Simpler just to wait for it before doing anything, even though it could slow start up slightly
        if (Plat_isCordova()) {
            // deviceready is weird and gets called even if the event has already passed
            await new Promise((resolve) => document.addEventListener('deviceready', resolve))
        }

        await Noti_registerMessageListeners()

        const timer = Logg_timer()

        // Want to be able to show the error screen if on load fails
        Erro_injectRenderCatchIntoBaseComponent()

        // Load persisted environment flags if set
        ENV = {
            ...ENV,
            ...pick(LS_envGet() ?? {}, 'locationPreferenceOverride'),
        }

        if (shouldUseSentry()) {
            sentry.register(keys.sentry, ENV.releaseVersion)
        }

        // Add Plat.isCordova className to body for platform specific styling
        const body = document.getElementsByTagName('body')[0]
        body.className = body.className + (Plat_isCordova() ? ' isCordova' : ' isNotCordova')

        LS_migrate()
        IDB_dexieInitialise()
        IDB_deviceDBInitialise()

        const db = await IDB_initialise()
        const hive = await hiveInitialise(db, reduxStore)

        await Auth_initialise()
        await Uplo_initialise()
        await Unit_initialise()

        Cust_initialise()
        audio.initialise()
        Sync_initialise(hive, bus)
        Todo_initialise(bus)
        Flow_initialise()
        Noti_initialise()
        selectorsInitialize()
        E2E_initialize(bus)
        Devi_initialise(bus)

        await Translations.init(ESystem.APP)

        const user = User_get()
        const userLang = user?.language || defaultLanguage
        Translations.changeLanguage(userLang)

        window.i18n = Translations.t
        await I18N_initialise()

        Evnt_addHiveToWindow(hive, bus)

        const serverPrefs = LS_serverPrefsGet() || {}
        applyTheme(serverPrefs.theme)
        DS_setData('ServerPrefsFetched', () => ({ serverPrefs }))

        Rpts_initialise()

        // Zendesk Chat
        if (LaunchDarkly.getFlagValue('zendesk-chat-in-app', false)) {
            ZendeskWebChat.initChat(userLang)
            ZendeskWebChat.showChat()
        }

        await completeAppInit(bus, reduxStore)

        if (Plat_isCordova()) {
            // Hide the splash screen to see user feedback
            if (navigator && navigator.splashscreen) {
                navigator.splashscreen.hide()
            }
        }

        // Start checking inactivity
        Devi_inactivityCheck()

        DS_setData('OnLoadSucceeded', (d) => ({
            forcedLogoutCanLogoutWithoutWarning: User_isLoggedIn(d),
            onLoad: { status: 'success' },
        }))

        Logg_event('OnLoadComplete', { time: timer.getDurationInWholeSeconds() })
    } catch (error) {
        DS_setData('OnLoadFailed', () => ({
            onLoad: { status: 'failure', error },
        }))
        sentry.err(error, {})
    }
}

/**
 * Trigger next step in process after start up based on the auth state
 */
const completeAppInit = async (bus: Base_Bus, reduxStore: Store<IState>) => {
    await Imag_initialise()
    await UserProfileImageCache.reInitialise()

    // Get GPS access for coordinates
    createCoordinatesWatch(reduxStore)

    const authStatus = Auth_getStatus()

    switch (authStatus) {
        case 'ready':
            // The user is not logged in so needs to navigate to auth server to login
            Auth_navigateLogin()
            break

        case 'pending-automatic-login':
            // Automatic login requested so need to authenticate via the auth server
            await Auth_safeLogout()
            Auth_navigateLogin()
            break

        case 'pending-sync':
            // The user has come back from the auth server with token and needs to login for the first time
            // (Browser only because on cordova the page does not need to be reloaded)
            Auth_loginSync(bus)
            break

        case 'complete':
            // The user is already logged in

            // Load legacy data into memory
            globalHive.dispatch(syncAll())
            Acti_initialise(globalHive)

            // Recover path after Android kill
            const recoveredPath = await Plat_recoverPath()

            // Navigate to start page if at root
            const hash = location.hash.replace(/\?.*/, '').replace(/^#/, '')
            if (hash === '' || hash === '/') await Rout_toHome(true)

            // After routing to home, route to recovered path
            // so they can press back to get to home
            if (recoveredPath != null) Rout_to(recoveredPath)

            // Zendesk Chat
            ZendeskWebChat.hideChat()

        case 'dev-select':
            // Wait for the developer to pick how to login (remote/local, field/manufacturing...etc)
            break

        case 'logged-out':
            // Wait for user to read message about being logged out and press button to start login again
            break

        case 'failure':
        case 'syncing':
        case 'waiting-for-internet':
        case 'waiting-for-redirect':
            // Should not be in these states at this point :fingers-crossed:
            sentry.err(`Unexpected auth state: ${authStatus}`, {})
            break

        default:
            assumeExhaustion(authStatus)
            sentry.err(`Unhandled auth state: ${authStatus}`, {})
            break
    }
}
